import { usePromise } from './utilHooks';
import { Profile, User, UserManager } from 'oidc-client';
import { Config } from '../../models';
import { useConfig } from './config';
import { Tilgangsnode } from '../../models/Tilgangsnode';
import { useMemo } from 'react';
import { TilgangstypeEnum } from '../../models/Enum/Tilgangstype';
import { store } from '../store';
import { handleError } from '../../features/layout/layoutSlice';
import { checkTrailingSlash } from '../../api/client';
import { Buffer } from 'buffer';

export type Credentials = {
    user: User,
    client: UserManager,
    availableNodes: Tilgangsnode[]
};
let userManager: UserManager | null = null;
let authPromise: Promise<Credentials> | undefined = undefined;

async function getUser(client: UserManager): Promise<User> {
    try {
        const user = await client.signinCallback();

        if (user?.state) {
            window.history.replaceState(null, '', user.state);
        } else {
            window.history.replaceState(null, '', window.localStorage.pathname);
        }
        if (user != null && !user.expired) return user;

    } catch (e) {
        const err = e as Error;
        switch (err.message) {
            case 'No state in response':
                console.debug(err.message as string);
                break;
            case 'No matching state found in storage':
                console.warn(err.message, '- Will try to reauthenticate user');
                break;
            default:
                throw err;
        }
    }

    try {
        const user = await client.getUser();
        if (user != null && !user.expired) return user;
    } catch (err) {
        console.debug('No tokens in session storage, will try to retrieve tokens silently', err);
    }

    try {
        const user = await client.signinSilent();
        if (user != null && !user.expired) return user;

    } catch (err) {
        console.debug('Unable to sign in silently, will do full redirect login flow', err);
    }

    await client.removeUser();
    await client.clearStaleState();
    await client.signinRedirect({
        state: window.location.pathname + window.location.search + window.location.hash,
    });

    // Will never resolve so that we keep the spinner going until the page is redirected to the login provider
    return new Promise<User>(() => {
    });
}

function getUserManager(config: Config): UserManager {
    if (!userManager) {
        let acrValues: string | undefined;
        if(config.auth?.tenant) {
            acrValues = `tenant:${encodeURIComponent(config.auth.tenant)}`;
        }
        userManager = new UserManager({
            authority: checkTrailingSlash(config.auth?.authority || config.urls.authority),
            client_id: config.auth?.client_id || 'WebsakAdmin',
            redirect_uri: document.baseURI,
            response_type: 'code',
            scope: config.auth?.scope || 'openid Acos.Websak.Admin.User konfigurering offline_access indeksering search_admin',
            loadUserInfo: false,
            acr_values: acrValues,
        });
    }
    return userManager;
}

async function getTilganger(access_token: string, config: Config) {
    try {
        const res = await fetch(`${checkTrailingSlash(config.urls.api)}me/tilganger/`, {
            headers: {
                Authorization: `Bearer ${access_token}`
            }
        });
        const data = await res.json();
        if (res.ok) return data;
        else await store.dispatch(handleError(data as Error));
    } catch (e) { /**/
    }
}

async function reSign(config: Config) {
    const client = getUserManager(config);
    try {
        const authResult: Credentials = {
            client,
            user: await getUser(client),
            availableNodes: []

        };
        authResult.availableNodes = await getTilganger(authResult.user.access_token, config);
        return authResult;
    } catch (e) {
        console.debug(e);
    }

    await client.removeUser();
    await client.clearStaleState();
    await client.signinRedirect({
        state: window.location.pathname + window.location.search + window.location.hash,
    });
    // Will never resolve so that we keep the spinner going until the page is redirected to the login provider
    return new Promise<Credentials>(() => {
    });
}

async function refreshAuth(config: Config) {
    authPromise = reSign(config);
}

async function signIn(config: Config): Promise<Credentials> {
    const client = getUserManager(config);
    const authResult: Credentials = {
        client,
        user: await getUser(client),
        availableNodes: []
    };

    authResult.availableNodes = await getTilganger(authResult.user.access_token, config);

    client.events.addAccessTokenExpired(() => refreshAuth(config));

    client.events.addUserLoaded(u => {
        authResult.user = u;
    });

    return authResult;
}


export function getAuth(config: Config) {
    if (!authPromise) {
        authPromise = signIn(config);
    }
    return authPromise;
}

export function useAuth(): Credentials {
    const config = useConfig();
    return usePromise(getAuth(config));
}

export function useHentTilgangstypeForNode(nodeId: string): TilgangstypeEnum {
    const auth = useAuth();
    if (!Array.isArray(auth?.availableNodes)) return TilgangstypeEnum.IngenTilgang;
    else return auth.availableNodes.find(i => i.nodeId.toLowerCase() === nodeId.toLowerCase())?.tilgangstype ?? TilgangstypeEnum.IngenTilgang;
}


export function useGetUser(): Profile {
    const auth = useAuth();
    return useMemo(() => {
        const userClaims = auth.user.access_token.split('.')[1];
        var user: Profile = JSON.parse(Buffer.from(userClaims, 'base64').toString());
        return user;

    }, [auth]);
}
